import { CloseIcon, FilterNewIcon } from '@src/svg';
import { idType, selectItemType } from '@src/types';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { PlusIcon } from '@svg/PlusIcon';
import SectionTitle from '@uikit/Section/SectionTitle/SectionTitle';
import {
  FilterErrorIndicator,
  filterOperators,
  filterOptionType,
  getAvailableOperators,
  getFilterSelectData,
  OperatorInfo,
} from '@src/uikit/Tables/Filtration/constants';
import React, {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './style.scss';
import { toast } from 'react-toastify';
import { selectGlobal } from '@store/slices/global/global';
import AuthContactsAttach from '@component/LightVersion/AuthContactsLightNew/AuthContactsAttach';
import { useLocation } from 'react-router-dom';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { logger } from '@utils/logger';
import { FilterPopup } from '@uikit/Tables/Filtration/filterWraper/FilterPopup';
import {
  formatDateWithCurrentTime,
  getCurrentFilterValue,
  isFilledDateValue,
} from '@uikit/Tables/helpers';
import { IFilterField, ItemValue, MappedEntry } from '@uikit/Tables/types';
import { BaseButton } from '@uikit/Button/BaseButton';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

const FILTER_SUBMIT_ERROR_COOLDOWN_MS = 5000;

const FilterField: FC<IFilterField> = ({
  filteredItems,
  handleAddNewItem,
  sortingTable,
  openModal,
  modalName,
  requestTablePageChange,
  page,
  tableInfo,
  title,
  open,
  size,
  path,
  setOpen,
  fieldColumns,
  columnsSorting,
  setColumnsSorting,
  hasCreatePermission,
  tableName,
  addBtnTitle,
  buttonsClassName = '',
  argument = {},
  isFilterable = true,
  isFilterDisabled = false,
  disableAppliedSelectFilters = false,
  titleAdditionalElement,
  dataTestId,
}) => {
  const location = useLocation();
  const [errorIndicator, setErrorIndicator] = useState<FilterErrorIndicator>({
    field: null,
    operator: null,
    value: null,
  });
  const dispatch = useAppDispatch();
  const addBtnRef = useRef<any>();
  const submitCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousAppliedFiltersCountRef = useRef<number>(0);
  const { currentPath } = useAppSelector(selectGlobal);

  const [groupFilter, setGroupFilter] = useState<OperatorInfo | null>(null);
  const [isOpenContactsDropDown, setIsOpenContactsDropDown] =
    useState<boolean>(false);
  const [selectedColumn, setSelectedColumn] = useState<filterOptionType | null>(
    null
  );
  const [selectedOperator, setSelectedOperator] =
    useState<selectItemType | null>(null);
  const [filterDraftResetKey, setFilterDraftResetKey] = useState(0);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [hasSubmittedFilterDraft, setHasSubmittedFilterDraft] = useState(false);
  const [inputValue, setInputValue] = useState<
    | string
    | number
    | null
    | Date
    | Date[]
    | number[]
    | { id: idType; value: boolean; label: string }
  >(null);

  useOutsideClick({
    ref: addBtnRef,
    onOutsideClick: () => setIsOpenContactsDropDown(false),
  });

  useEffect(() => {
    if (isFilterDisabled && open) {
      setOpen(false);
    }
  }, [isFilterDisabled, open, setOpen]);

  const clearSubmitCooldown = useCallback(() => {
    if (submitCooldownRef.current) {
      clearTimeout(submitCooldownRef.current);
      submitCooldownRef.current = null;
    }

    setIsSubmitDisabled(false);
  }, []);

  const startSubmitErrorCooldown = useCallback(() => {
    setIsSubmitDisabled(true);

    if (submitCooldownRef.current) {
      clearTimeout(submitCooldownRef.current);
    }

    submitCooldownRef.current = setTimeout(() => {
      setIsSubmitDisabled(false);
      submitCooldownRef.current = null;
    }, FILTER_SUBMIT_ERROR_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (submitCooldownRef.current) {
        clearTimeout(submitCooldownRef.current);
      }
    };
  }, []);

  const resetFilterDraft = useCallback(() => {
    setSelectedColumn(null);
    setSelectedOperator(null);
    setInputValue(null);
    setGroupFilter(null);
    setHasSubmittedFilterDraft(false);
    setErrorIndicator({ field: null, operator: null, value: null });
    setFilterDraftResetKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    clearSubmitCooldown();
    setHasSubmittedFilterDraft(false);
  }, [selectedColumn, selectedOperator, inputValue, clearSubmitCooldown]);

  const isCompleteDateRange = (value: unknown): boolean =>
    Array.isArray(value) &&
    value.length >= 2 &&
    isFilledDateValue(value[0]) &&
    isFilledDateValue(value[1]);

  const isFilterValueFilled = (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.length > 0 && value.every((item) => isFilledDateValue(item));
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return value !== null && value !== undefined;
  };

  const normalizeFilterIdentifier = useCallback(
    (value?: string | null) => value?.toLowerCase()?.trim() || '',
    []
  );

  const normalizeComparableValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase().trim();
  }, []);

  const mappedObject: Array<MappedEntry> = useMemo(() => {
    const firstItem = filteredItems?.[0] as
      | Record<string, ItemValue>
      | undefined;

    if (!firstItem) return [];

    return Object.entries(firstItem).map(
      ([key, { operator = '', value = '' }]) => {
        const currentFilter = fieldColumns?.find((el) => {
          const normalizedKey = normalizeFilterIdentifier(key);

          return [
            normalizeFilterIdentifier(el.filterKey),
            normalizeFilterIdentifier(el.name),
            normalizeFilterIdentifier(el.label),
          ].includes(normalizedKey);
        });

        const {
          label: name = '',
          filterSelectName = '',
          filterType = '',
        } = currentFilter || {
          label: '',
          filterSelectName: '',
          filterType: '',
        };

        return {
          key,
          value,
          name,
          operator: operator || '',
          renderValue:
            getCurrentFilterValue(
              value,
              filterSelectName,
              operator,
              filterType,
              groupFilter,
              currentFilter?.filterOptions
            ) || '',
          renderOperator:
            Array.isArray(value) && operator !== 'range'
              ? 'Equals'
              : filterOperators.find((el) => el.value === operator)?.label ||
                '',
        };
      }
    );
  }, [filteredItems, fieldColumns, groupFilter, normalizeFilterIdentifier]);

  const selectedFilterKeys = useMemo(() => {
    const firstItem = filteredItems?.[0] as
      | Record<string, ItemValue>
      | undefined;

    if (!firstItem) return new Set<string>();

    const isNonEmptyFilterValue = (value: unknown): boolean => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    };

    const hasAllSelectOptionsSelected = (
      column: filterOptionType,
      value: unknown
    ): boolean => {
      if (!Array.isArray(value) || value.length === 0) return false;

      const availableOptions = column.filterOptions?.length
        ? column.filterOptions
        : getFilterSelectData(column.filterSelectName || '');

      if (!availableOptions?.length) return false;

      const selectedValues = new Set<string>(
        value.map((item) => normalizeComparableValue(item)).filter(Boolean)
      );

      const availableValues = new Set<string>(
        availableOptions
          .map((option) =>
            normalizeComparableValue(option.id ?? option.value ?? '')
          )
          .filter(Boolean)
      );

      if (!availableValues.size) return false;

      return [...availableValues].every((optionValue) =>
        selectedValues.has(optionValue)
      );
    };

    const selectedKeys = (fieldColumns || []).flatMap((column) => {
      const currentFilterEntry = Object.entries(firstItem).find(
        ([key, item]) => {
          if (!isNonEmptyFilterValue(item?.value)) return false;

          const normalizedKey = normalizeFilterIdentifier(key);

          return [
            normalizeFilterIdentifier(column.filterKey),
            normalizeFilterIdentifier(column.name),
            normalizeFilterIdentifier(column.label),
          ].includes(normalizedKey);
        }
      );

      if (!currentFilterEntry) return [];

      const [, currentFilterValue] = currentFilterEntry;
      const shouldDisable =
        column.filterType === 'select'
          ? disableAppliedSelectFilters ||
            hasAllSelectOptionsSelected(column, currentFilterValue?.value)
          : true;

      if (!shouldDisable) return [];

      return [
        normalizeFilterIdentifier(column.filterKey),
        normalizeFilterIdentifier(column.name),
        normalizeFilterIdentifier(column.label),
      ].filter(Boolean);
    });

    return new Set(selectedKeys);
  }, [
    filteredItems,
    fieldColumns,
    disableAppliedSelectFilters,
    normalizeFilterIdentifier,
    normalizeComparableValue,
  ]);

  const fieldColumnsWithDisabledSelected = useMemo(
    () =>
      fieldColumns?.map((column) => {
        const isAlreadySelected = [
          normalizeFilterIdentifier(column.filterKey),
          normalizeFilterIdentifier(column.name),
          normalizeFilterIdentifier(column.label),
        ].some((identifier) => selectedFilterKeys.has(identifier));

        return {
          ...column,
          isDisabled: isAlreadySelected,
          disabled: isAlreadySelected,
        };
      }),
    [fieldColumns, selectedFilterKeys, normalizeFilterIdentifier]
  );

  useEffect(() => {
    if (!selectedColumn) return;

    const isAlreadySelected = [
      normalizeFilterIdentifier(selectedColumn.filterKey),
      normalizeFilterIdentifier(selectedColumn.name),
      normalizeFilterIdentifier(selectedColumn.label),
    ].some((identifier) => selectedFilterKeys.has(identifier));

    if (!isAlreadySelected) return;

    setSelectedColumn(null);
    setSelectedOperator(null);
    setInputValue(null);
  }, [selectedColumn, selectedFilterKeys, normalizeFilterIdentifier]);

  useEffect(() => {
    const previousAppliedFiltersCount = previousAppliedFiltersCountRef.current;

    if (previousAppliedFiltersCount > 0 && mappedObject.length === 0) {
      resetFilterDraft();
    }

    previousAppliedFiltersCountRef.current = mappedObject.length;
  }, [mappedObject.length, resetFilterDraft]);

  useEffect(() => {
    if (!selectedColumn) {
      setGroupFilter(null);
      return;
    }
    setGroupFilter(getAvailableOperators(selectedColumn));
  }, [selectedColumn]);

  useEffect(() => {
    if (!selectedColumn || !groupFilter) return;
    if (inputValue !== null && inputValue !== undefined) return;

    const selectedColumnIdentifiers = [
      normalizeFilterIdentifier(selectedColumn.filterKey),
      normalizeFilterIdentifier(selectedColumn.name),
      normalizeFilterIdentifier(selectedColumn.label),
    ].filter(Boolean);

    const currentFilter = mappedObject.find(({ key }) =>
      selectedColumnIdentifiers.includes(normalizeFilterIdentifier(key))
    );

    if (!currentFilter) return;

    if (!selectedOperator?.value) {
      const matchedOperator = groupFilter.operators.find(
        (operator) =>
          normalizeComparableValue(operator.value) ===
          normalizeComparableValue(currentFilter.operator)
      );

      if (matchedOperator) {
        setSelectedOperator(matchedOperator);
      }
    }

    if (
      selectedColumn.filterType === 'select' &&
      Array.isArray(currentFilter.value)
    ) {
      const availableOptions = selectedColumn.filterOptions?.length
        ? selectedColumn.filterOptions
        : getFilterSelectData(selectedColumn.filterSelectName || '');

      const selectedValues = new Set(
        currentFilter.value
          .map((item) => normalizeComparableValue(item))
          .filter(Boolean)
      );

      const mappedSelectedOptions = availableOptions
        .filter((option) => {
          const optionId = normalizeComparableValue(option.id);
          const optionValue = normalizeComparableValue(option.value);
          return (
            selectedValues.has(optionId) || selectedValues.has(optionValue)
          );
        })
        .map((option) => ({
          ...option,
          value: option.value ?? option.id,
          label: option.label ?? option.value,
        }));

      if (mappedSelectedOptions.length) {
        setInputValue(mappedSelectedOptions);
      }
    }
  }, [
    selectedColumn,
    groupFilter,
    inputValue,
    mappedObject,
    selectedOperator?.value,
    normalizeFilterIdentifier,
    normalizeComparableValue,
  ]);

  useEffect(() => {
    setSelectedOperator(null);
    setInputValue(null);
    setErrorIndicator((prev) => ({
      ...prev,
      field: selectedColumn ? false : prev.field,
      operator: null,
      value: null,
    }));

    if (!selectedColumn) return;

    const groupFilters = getAvailableOperators(selectedColumn);
    if (groupFilters?.operators?.length === 1) {
      setSelectedOperator(groupFilters.operators[0]);
    }
  }, [selectedColumn?.filterKey, selectedColumn?.name]);

  useEffect(() => {
    if (!selectedColumn || !fieldColumns?.length) return;

    const updatedSelectedColumn = fieldColumns.find(
      (column) =>
        (column.filterKey || column.name) ===
        (selectedColumn.filterKey || selectedColumn.name)
    );

    if (
      updatedSelectedColumn &&
      (updatedSelectedColumn.filterOptions !== selectedColumn.filterOptions ||
        updatedSelectedColumn.requestByAsync !== selectedColumn.requestByAsync)
    ) {
      setSelectedColumn(updatedSelectedColumn);
    }
  }, [fieldColumns, selectedColumn]);

  const createFilterObject = useCallback(() => {
    return mappedObject.reduce(
      (acc, { key, operator, value }) => {
        acc[key] = { operator, value };
        return acc;
      },
      {} as Record<string, ItemValue>
    );
  }, [mappedObject]);

  const visibleErrorIndicator = useMemo(
    () =>
      hasSubmittedFilterDraft
        ? errorIndicator
        : { field: null, operator: null, value: null },
    [hasSubmittedFilterDraft, errorIndicator]
  );

  useEffect(() => {
    if (!selectedOperator || !selectedColumn) return;
    const filterObject = createFilterObject();
    setErrorIndicator((prev) => ({
      ...prev,
      field: false,
      operator: false,
      value: !isFilterValueFilled(inputValue),
    }));
    const columnKey = groupFilter?.key || selectedColumn.name;

    setColumnsSorting((prev: any) => ({
      ...prev,
      [tableName]: {
        ...filterObject,
        [columnKey]: {
          operator: selectedOperator.value,
          value: prev[tableName]?.[columnKey]?.value || inputValue,
        },
      },
    }));
  }, [selectedOperator]);

  useEffect(() => {
    if (!selectedColumn || !selectedOperator) return;
    setColumnsSorting((prev) => {
      const existingFiltersForTable = prev[tableName]
        ? { ...prev[tableName] }
        : {};
      const columnKey = groupFilter?.key || selectedColumn.name;

      let processedValue: any;

      if (inputValue !== null && inputValue !== undefined) {
        if (selectedColumn?.filterType === 'date') {
          if (Array.isArray(inputValue)) {
            processedValue = inputValue.map(
              (dateStr, index) =>
                formatDateWithCurrentTime(
                  dateStr,
                  selectedOperator?.value === 'range' && index === 1
                    ? 'end'
                    : 'start'
                ) || ''
            );
          } else {
            processedValue =
              formatDateWithCurrentTime(inputValue as string) || '';
          }
        } else if (typeof inputValue === 'string') {
          processedValue = inputValue.trim();
        } else if (Array.isArray(inputValue)) {
          processedValue = inputValue.map((item) => {
            if (typeof item === 'string') return item.trim();
            if (typeof item === 'object' && item !== null && 'id' in item) {
              return (item as { id: number }).id;
            }
            return item;
          });
        } else if (typeof inputValue === 'object' && 'value' in inputValue) {
          processedValue = (inputValue as any).value;
        } else {
          processedValue = inputValue;
        }
      }

      if (
        (typeof processedValue === 'string' && processedValue === '') ||
        (Array.isArray(processedValue) && processedValue?.length === 0) ||
        processedValue === undefined ||
        processedValue === null
      ) {
        const { [columnKey]: _, ...newFilters } = existingFiltersForTable;
        return { ...prev, [tableName]: newFilters };
      }

      return {
        ...prev,
        [tableName]: {
          ...existingFiltersForTable,
          [columnKey]: {
            operator: selectedOperator?.value,
            value: processedValue,
          },
        },
      };
    });
  }, [
    inputValue,
    groupFilter,
    selectedOperator,
    selectedColumn,
    tableName,
    setColumnsSorting,
  ]);

  const onSubmit = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();

      if (isSubmitDisabled) return;

      setHasSubmittedFilterDraft(true);

      const isDateRangeFilter =
        selectedOperator?.value === 'range' &&
        (selectedColumn?.filterType === 'date' ||
          selectedColumn?.filterType === 'dateTime');

      if (isDateRangeFilter && !isCompleteDateRange(inputValue)) {
        setErrorIndicator({
          field: !selectedColumn?.name,
          operator: !selectedOperator?.value,
          value: true,
        });
        toast.error(toastMessage.COMMON.selectStartAndEndDates);
        startSubmitErrorCooldown();
        return;
      }

      const hasSelectedColumn = !!selectedColumn?.name;
      const hasSelectedOperator = !!selectedOperator?.value;
      const hasInputValue = isFilterValueFilled(inputValue);

      if (hasSelectedOperator && hasSelectedColumn && hasInputValue) {
        setIsSubmitDisabled(true);

        try {
          await dispatch(
            requestTablePageChange.initiate({
              requestId: Date.now(),
              id: 2,
              pathName: path,
              type: path,
              argument: { type: path, ...argument },
              body: columnsSorting[tableName],
              params: { page, size, ...sortingTable },
            })
          ).unwrap();
          clearSubmitCooldown();
        } catch (err: any) {
          logger.error('Unexpected error', err);
          toastError(err, toastMessage.COMMON.failedToLoadTableData);
          startSubmitErrorCooldown();
        } finally {
          setErrorIndicator({ field: false, operator: false, value: false });
        }
      } else {
        setErrorIndicator({
          field: !hasSelectedColumn,
          operator: !hasSelectedOperator,
          value: !hasInputValue,
        });
      }
    },
    [
      selectedOperator,
      selectedColumn,
      inputValue,
      isSubmitDisabled,
      dispatch,
      requestTablePageChange,
      path,
      argument,
      columnsSorting,
      tableName,
      page,
      size,
      sortingTable,
      clearSubmitCooldown,
      startSubmitErrorCooldown,
    ]
  );

  const handleClose = useCallback(async () => {
    resetFilterDraft();
    setOpen(false);
  }, [resetFilterDraft, setOpen]);

  const handleDelete = useCallback(
    async (key: string, tableName: string) => {
      const { [key]: deletedKey, ...remainingFilters } = mappedObject.reduce(
        (acc, item) => {
          acc[item.key] = { operator: item.operator, value: item.value };
          return acc;
        },
        {} as Record<string, any>
      );

      const hasRemainingFilters = Object.keys(remainingFilters).length > 0;
      const updatedColumnsSorting = hasRemainingFilters
        ? { [tableName]: remainingFilters }
        : {};

      resetFilterDraft();
      setColumnsSorting(updatedColumnsSorting);

      try {
        await dispatch(
          requestTablePageChange.initiate({
            requestId: Date.now(),
            id: 2,
            pathName: path,
            type: path,
            argument: { ...argument },
            body: remainingFilters,
            params: { page, size, ...sortingTable },
          })
        );
      } catch (err) {
        logger.error('Failed to update table filters:', err);
      }
    },
    [
      mappedObject,
      resetFilterDraft,
      setColumnsSorting,
      dispatch,
      requestTablePageChange,
      path,
      argument,
      page,
      size,
      sortingTable,
    ]
  );

  return (
    <>
      <SectionTitle
        className="table-title"
        title={title}
        info={tableInfo}
        dataTestId={`${dataTestId}-field-section-title`}
      >
        {titleAdditionalElement}
        <div className="filter-position">
          {open && (
            <FilterPopup
              filterDraftResetKey={filterDraftResetKey}
              dataTestId={dataTestId}
              groupFilter={groupFilter}
              inputValue={inputValue}
              handleClose={handleClose}
              selectedColumn={selectedColumn}
              selectedOperator={selectedOperator}
              className="select-filter-popup"
              setErrorIndicator={setErrorIndicator}
              setSelectedColumn={setSelectedColumn}
              setSelectedOperator={setSelectedOperator}
              setInputValue={setInputValue}
              fieldColumns={fieldColumnsWithDisabledSelected}
              errorIndicator={visibleErrorIndicator}
              tableInfo={tableInfo}
              onSubmit={onSubmit}
              isSubmitDisabled={isSubmitDisabled}
            />
          )}

          <div
            className="buttons_container"
            data-test-id={`${dataTestId}-buttons-container`}
            onClick={(e) => e.stopPropagation()}
          >
            {hasCreatePermission && (
              <button
                data-test-id={`${dataTestId}-add-button`}
                className="filter_button"
                ref={addBtnRef}
                onClick={() => {
                  if (
                    (currentPath === 'standingOrder' ||
                      currentPath === 'ride') &&
                    location.pathname.includes('contacts') &&
                    modalName !== 'notes'
                  ) {
                    setIsOpenContactsDropDown(!isOpenContactsDropDown);
                  } else {
                    openModal && openModal(modalName, null);
                    handleAddNewItem && handleAddNewItem();
                  }
                }}
              >
                <PlusIcon width={16} height={16} />
                {addBtnTitle ? `Add ${addBtnTitle}` : null}
                {isOpenContactsDropDown && (
                  <AuthContactsAttach
                    setAttachedContact={async (id) => {
                      setIsOpenContactsDropDown(false);
                      return openModal && (await openModal(null, id));
                    }}
                    dataTestId={`${dataTestId}-attach-contact`}
                  />
                )}
              </button>
            )}
            {isFilterable && (
              <BaseButton
                Icon={<FilterNewIcon />}
                className={`filter_button ${isFilterDisabled ? 'disabled' : ''}`}
                onClick={() => !isFilterDisabled && setOpen(true)}
                disabled={isFilterDisabled}
                dataTestId={`${dataTestId}-filter-button`}
                title="Filter"
              />
            )}
          </div>
        </div>
      </SectionTitle>
      {!!mappedObject.length && (
        <div className={`buttons_filter ${buttonsClassName}`}>
          {mappedObject.map(({ key, name, renderValue, renderOperator }) => (
            <div className="filter-cover" key={key}>
              <div className="filtered-button_wrapper">
                <button className="filtered-button">
                  <strong>
                    {name}: {renderOperator} - {renderValue}
                  </strong>
                  <div
                    data-test-id={`${dataTestId}-delete-button`}
                    className="close-button"
                    onClick={() => handleDelete(key, tableName)}
                  >
                    <CloseIcon width={19} height={19} color="#fff" />
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FilterField;
